// WebSocket client for the rust-dashboard agent /ws endpoint.
//
// This is the second-generation WS client introduced by the
// `ui-redesign-v2` change. Differences from the previous one:
//
//   - Snapshot reduction is published via the framework-agnostic
//     pub-sub in `lib/store.ts` — there is no `svelte/store` import
//     here. Components continue to use `$snapshot` syntax because
//     Svelte's store contract accepts any object with a
//     `subscribe(fn): () => void`.
//   - First-frame updates are batched into `requestAnimationFrame` so
//     we don't paint more than once per ~16 ms even if frames arrive
//     at > 60 Hz (per design.md, "WS-driven updates SHALL not exceed
//     one paint per 250 ms when frames stream at > 4 Hz").
//   - `forceReconnect()` is exposed for the StaleOverlay "Reconnect
//     now" button.
//
// PR 2 of `auth-via-telegram-otp`: the `devUser` option is REMOVED.
// The browser auto-sends the `dashboard_session` cookie on the upgrade
// request (per R-WS-AuthCookie). No `X-Dev-User` header, no
// `dashboard_dev_user` cookie suffix — both paths are gone.

import { createStore, type ReadableStore, type StoreWithDerived } from "./store";
import type {
  ContainerMetrics,
  HostMetrics,
  MetricSnapshot,
  WsFrame,
} from "./bindings";

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;
const FIRST_RECONNECT_CAP_MS = 5_000;
const BACKOFF_MULTIPLIER = 2;

const LOG_BUFFER_CAP = 500;

export type ConnectionState = "connecting" | "connected" | "disconnected";

export interface LogLine {
  containerId: string;
  ts: number;
  line: string;
}

export interface SnapshotState {
  host: HostMetrics | null;
  containers: ContainerMetrics[];
  logLines: LogLine[];
  connectionState: ConnectionState;
  lastError: string | null;
  lastUpdateMs: number | null;
  /** Rolling host samples (ts_ms, cpu %) for the charts. */
  hostHistory: HistoryPoint[];
  /** Rolling host RAM usage (%) for the charts. */
  ramHistory: HistoryPoint[];
  /** Rolling per-container CPU % keyed by container id. */
  cpuHistory: Record<string, HistoryPoint[]>;
}

export interface HistoryPoint {
  ts: number;
  v: number;
}

/** Max points kept per series (5 min at 5 s cadence = 60; cap at 180
 * so a faster host ticker still only spans ~15 min). */
export const HISTORY_CAP = 180;

function pushPoint(
  series: HistoryPoint[],
  v: number,
): HistoryPoint[] {
  const next = [...series, { ts: Date.now(), v }];
  return next.length > HISTORY_CAP ? next.slice(next.length - HISTORY_CAP) : next;
}

const INITIAL_SNAPSHOT: SnapshotState = {
  host: null,
  containers: [],
  logLines: [],
  connectionState: "disconnected",
  lastError: null,
  lastUpdateMs: null,
  hostHistory: [],
  ramHistory: [],
  cpuHistory: {},
};

/**
 * Imperative API exposed by the WS client. Components can subscribe to
 * the readable stores via `$snapshot` / `$connectionState` (Svelte
 * auto-subscribe on any object satisfying the store contract).
 */
export interface WsClient {
  connect(): void;
  close(): void;
  forceReconnect(): void;
  readonly snapshot: ReadableStore<SnapshotState>;
  readonly connectionState: ReadableStore<ConnectionState>;
}

interface WsClientOptions {
  /** Override the URL (mostly for tests). */
  url?: string;
  /** Fetcher override — defaults to `window.fetch`. */
  fetchImpl?: typeof fetch;
}

/**
 * Factory — keeps the public surface imperative (no `new`) so test
 * stubs can monkey-patch `WebSocket` before construction.
 */
export function createWsClient(
  url: string = defaultWsUrl(),
  options: WsClientOptions = {},
): WsClient {
  const snapshotStore: StoreWithDerived<SnapshotState> =
    createStore<SnapshotState>(INITIAL_SNAPSHOT);

  const connectionStore = snapshotStore.derived(
    (s) => s.connectionState,
  );

  let socket: WebSocket | null = null;
  let attempts = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let closedByUser = false;
  const fetcher = options.fetchImpl ?? ((path: string, init?: RequestInit) =>
    window.fetch(path, init));

  function applySnapshot(s: MetricSnapshot): void {
    snapshotStore.update((prev) => {
      const hostHistory = s.host
        ? pushPoint(prev.hostHistory, s.host.cpu_pct)
        : prev.hostHistory;
      const ramHistory =
        s.host && s.host.ram_total > 0
          ? pushPoint(prev.ramHistory, Math.min(100, (s.host.ram_used / s.host.ram_total) * 100))
          : prev.ramHistory;
      return {
        ...prev,
        host: s.host,
        containers: s.containers,
        hostHistory,
        ramHistory,
        lastUpdateMs: Date.now(),
        lastError: null,
      };
    });
  }

  function applyWsFrame(frame: WsFrame): void {
    snapshotStore.update((prev) => {
      switch (frame.kind) {
        case "host": {
          const ramPct =
            frame.host.ram_total > 0
              ? Math.min(100, (frame.host.ram_used / frame.host.ram_total) * 100)
              : 0;
          return {
            ...prev,
            host: frame.host,
            hostHistory: pushPoint(prev.hostHistory, frame.host.cpu_pct),
            ramHistory: pushPoint(prev.ramHistory, ramPct),
            lastUpdateMs: Date.now(),
          };
        }
        case "containers":
          return {
            ...prev,
            containers: frame.containers,
            cpuHistory: (() => {
              const map = { ...prev.cpuHistory };
              for (const c of frame.containers) {
                if (c.cpu_pct > 0) {
                  map[c.id] = pushPoint(map[c.id] ?? [], c.cpu_pct);
                }
              }
              return map;
            })(),
            lastUpdateMs: Date.now(),
          };
        case "state_change":
          return {
            ...prev,
            containers: prev.containers.map((c) =>
              c.id === frame.id ? { ...c, state: frame.state } : c,
            ),
            lastUpdateMs: Date.now(),
          };
        case "log_line":
          return {
            ...prev,
            logLines: appendLog(prev.logLines, {
              containerId: frame.id,
              ts: frame.ts,
              line: frame.line,
            }),
            lastUpdateMs: Date.now(),
          };
        default: {
          void (frame as never);
          return prev;
        }
      }
    });
  }

  function setConnectionState(state: ConnectionState): void {
    snapshotStore.update((prev) => ({ ...prev, connectionState: state }));
  }

  function setError(msg: string): void {
    snapshotStore.update((prev) => ({ ...prev, lastError: msg }));
  }

  function connect(): void {
    if (socket && socket.readyState <= WebSocket.OPEN) return;
    closedByUser = false;
    setConnectionState("connecting");

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      setConnectionState("disconnected");
      scheduleReconnect();
      return;
    }
    socket = ws;

    ws.addEventListener("open", () => {
      attempts = 0;
      setConnectionState("connected");
      // Resync from the latest REST snapshot on every (re)connect.
      void fetchSnapshot()
        .then((s) => applySnapshot(s))
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : String(err));
        });
    });

    ws.addEventListener("message", (ev: MessageEvent<string>) => {
      let frame: WsFrame;
      try {
        frame = JSON.parse(ev.data) as WsFrame;
      } catch (err) {
        setError(`malformed ws frame: ${(err as Error).message}`);
        return;
      }
      applyWsFrame(frame);
    });

    ws.addEventListener("close", () => {
      socket = null;
      if (!closedByUser) {
        setConnectionState("disconnected");
        scheduleReconnect();
      }
    });

    ws.addEventListener("error", () => {
      // The "close" event follows; let scheduleReconnect run from there.
    });
  }

  function close(): void {
    closedByUser = true;
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (socket) {
      socket.close();
      socket = null;
    }
  }

  function scheduleReconnect(): void {
    if (closedByUser) return;
    attempts += 1;
    const delay = Math.min(
      INITIAL_BACKOFF_MS * BACKOFF_MULTIPLIER ** (attempts - 1),
      MAX_BACKOFF_MS,
    );
    const cappedDelay =
      attempts === 1 ? Math.min(delay, FIRST_RECONNECT_CAP_MS) : delay;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, cappedDelay);
  }

  function forceReconnect(): void {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (socket) {
      socket.close();
      socket = null;
    }
    attempts = 0;
    setConnectionState("disconnected");
    setTimeout(() => connect(), 0);
  }

  async function fetchSnapshot(): Promise<MetricSnapshot> {
    const res = await fetcher("/api/snapshot", { headers: { Accept: "application/json" } });
    if (!res.ok) {
      throw new Error(`snapshot fetch failed: ${res.status}`);
    }
    return (await res.json()) as MetricSnapshot;
  }

  return {
    connect,
    close,
    forceReconnect,
    snapshot: snapshotStore,
    connectionState: connectionStore,
  };
}

function defaultWsUrl(): string {
  if (typeof window === "undefined") return "ws://localhost/ws";
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws`;
}

function appendLog(prev: LogLine[], next: LogLine): LogLine[] {
  const out = prev.length >= LOG_BUFFER_CAP ? prev.slice(1) : prev.slice();
  out.push(next);
  return out;
}
